/**
 * Crops an image to a specific aspect ratio and reduces its file size. (only runs in the browser environment)
 *
 * @async
 * @function cropImage
 * @param {string} url - Input file path
 * @param {CropImageOptions} options - Options to configure output specifications
 * @returns {Promise<Blob|null>} A promise that resolves a blob object after the image is processed
 * @example
 * // without options
 * cropImage('http://your-image-url')
 *
 * // with options
 * cropImage('http://your-image-url', { quality: 0.5 })
 * @tutorial https://pqina.nl/blog/cropping-images-to-an-aspect-ratio-with-javascript/
 */
async function cropImage(url, options) {
    const inputImage = document.createElement('img');
    inputImage.src = url;
    return new Promise((resolve, reject) => {
        inputImage.onload = () => cropImageCallback(inputImage, resolve, options);
        inputImage.onerror = reject;
    });
}
function cropImageCallback(img, resolve, options) {
    const { width, height, mime = 'image/jpeg', quality = 0.75, aspectRatio = 1, } = options || {};
    // let's store the width and height of our image
    const inputWidth = img.naturalWidth;
    const inputHeight = img.naturalHeight;
    // create a canvas that will present the output image
    const outputImage = document.createElement('canvas');
    // set it to the same size as the image
    outputImage.width = width || inputWidth;
    outputImage.height = height || inputHeight;
    // draw our image at position 0, 0 on the canvas
    const ctx = outputImage.getContext('2d');
    // get the aspect ratio of the input image
    const inputImageAspectRatio = inputWidth / inputHeight;
    // if it's bigger than our target aspect ratio
    let outputWidth = inputWidth;
    let outputHeight = inputHeight;
    if (inputImageAspectRatio > aspectRatio) {
        outputWidth = inputHeight * aspectRatio;
    }
    else if (inputImageAspectRatio < aspectRatio) {
        outputHeight = inputWidth / aspectRatio;
    }
    // calculate the position to draw the image at
    const outputX = (inputWidth - outputWidth) * 0.5;
    const outputY = (inputHeight - outputHeight) * 0.5;
    ctx?.drawImage(img, outputX, outputY, outputWidth, outputHeight, 0, 0, outputImage.width, outputImage.height);
    outputImage.toBlob(resolve, mime, quality);
}

export { cropImage, cropImageCallback };
